/**
 * `StubChatbotTransport` — ships-today implementation of the
 * {@link ChatbotTransport} interface (design § Component 6, Wave 2 task 2.8).
 *
 * The real MCP client (from the deferred `chatbot-mcp` spec, priority P13)
 * will implement the same interface and drop into `DocsSliderProvider` with
 * zero UX change. Until then, this stub answers questions from a compile-time
 * canned table so the dock has something meaningful to render.
 *
 * ## Contract
 *
 * - Never sees the raw `ownerId` — receives `sessionId = hashSessionKey(ownerId)`
 *   instead (Constitution Rule II, design § Function F1, property P5).
 * - Never rejects — always resolves. Pathological input still yields a
 *   response; the user experience never bubbles a promise rejection into
 *   the dock.
 * - Never accepts or serialises `ownerId` or `authorization` — the
 *   {@link ChatbotSendInput} interface intentionally omits both. The
 *   property test P5 pins this at the type level via `Exclude<keyof …, …>`.
 * - Simulates 150 ms of latency so the dock's typing indicator has time
 *   to render (design § Component 7).
 *
 * @see .kiro/specs/help-docs-panel/design.md — § Component 6, § Function F1
 * @see .kiro/specs/help-docs-panel/requirements.md — Req 9.1–9.7
 * @see ./stubCannedTable.ts — the compile-time canned response table
 *
 * @packageDocumentation
 */

import { STUB_CANNED_TABLE, type StubCannedRow } from "./stubCannedTable";
import type { DocsSliderContext } from "./types";

// ---------------------------------------------------------------------------
// Public interfaces
// ---------------------------------------------------------------------------

/**
 * The shape sent to any {@link ChatbotTransport} implementation.
 *
 * @remarks
 * - `sessionId` MUST be a hashed, tenant-safe session key — never the raw
 *   `ownerId` (Constitution Rule II). The docs slider derives it via
 *   `hashSessionKey(ownerId)` and passes the resulting 16-char hex string.
 * - `ownerId` and `authorization` are intentionally NOT part of this
 *   interface. Property test P5 asserts the type shape excludes them.
 */
export interface ChatbotSendInput {
  /** Hashed session key (16 lowercase hex characters). Never the raw ownerId. */
  readonly sessionId: string;
  /** The user's message text, as typed into the dock's input. */
  readonly message: string;
  /** Optional {@link DocsSliderContext} — set when the dock was opened
   *  from an error card, deep link, or docs slider affordance. */
  readonly context?: DocsSliderContext;
}

/**
 * The shape returned by any {@link ChatbotTransport} implementation.
 *
 * @remarks
 * - `citedDocs` are manifest-relative paths (no leading `/`, no `..`,
 *   each ends in `.md`). Each becomes a `CitationChip` in the dock.
 * - `suggestions` are optional follow-up prompts; the stub does not emit
 *   any today, but the field is reserved for the real MCP client.
 */
export interface ChatbotResponse {
  /** The assistant's message body, rendered verbatim in the dock. */
  readonly text: string;
  /** Optional list of manifest-relative doc paths, e.g. `["api/statements.md"]`. */
  readonly citedDocs?: readonly string[];
  /** Optional follow-up prompts the dock renders as one-click suggestions. */
  readonly suggestions?: readonly string[];
}

/**
 * Transport interface used by `DocsSliderProvider` to talk to whatever
 * chatbot implementation is wired at the top of the app.
 *
 * @remarks
 * `StubChatbotTransport` (this module) and the real MCP client (from the
 * deferred `chatbot-mcp` spec) both implement this interface.
 */
export interface ChatbotTransport {
  /** Discriminator so callers can log or feature-flag on the transport type. */
  readonly kind: "stub" | "mcp";
  /** Send a message; returns the assistant's response. Never rejects. */
  send(input: ChatbotSendInput): Promise<ChatbotResponse>;
}

// ---------------------------------------------------------------------------
// Internal constants
// ---------------------------------------------------------------------------

/** The two error-handling docs added to every response when
 *  `context.errorId` is set. Order matters — the test asserts both are
 *  present, and manifest-relative paths never begin with `/`. */
const ERROR_HANDLING_DOCS: readonly string[] = Object.freeze([
  "error-handling/guide.md",
  "error-handling/categories.md",
]);

/** Text used when no keyword matches AND no `context.errorId` is set. */
const NO_MATCH_TEXT =
  "This is a stub chatbot — the real assistant ships with 'chatbot-mcp' in a later release. Meanwhile, try the docs panel on the right.";

/** Text used when there is no keyword match but the caller supplied an
 *  `errorId` in context — the dock still needs a body, and pointing the
 *  user at the error-handling docs is the most useful response. */
const ERROR_ONLY_TEXT =
  "Errors follow the platform error handling pattern — start with the error-handling guide and categories docs.";

/** Note appended to the response text when `sessionId` is not a valid
 *  16-char lowercase hex string (i.e. the caller bypassed
 *  `hashSessionKey`). We resolve rather than reject so the dock never
 *  bubbles a rejection to the user. */
const INVALID_SESSION_NOTE =
  " (session key invalid — falling back to global response)";

/** Simulated latency in milliseconds so the dock's typing indicator has
 *  time to render at least one frame. */
const SIMULATED_LATENCY_MS = 150;

/** Precondition regex for `sessionId`. Matches the exact output shape of
 *  `hashSessionKey` (Algorithm F1). */
const SESSION_KEY_PATTERN = /^[a-f0-9]{16}$/;

// ---------------------------------------------------------------------------
// StubChatbotTransport
// ---------------------------------------------------------------------------

/**
 * Ships-today stub transport. See module docstring for the contract.
 *
 * @example
 * ```ts
 * const transport = new StubChatbotTransport();
 * const response = await transport.send({
 *   sessionId: hashSessionKey(user.id),
 *   message: "tell me about statements",
 * });
 * // response.citedDocs includes "api/statements.md" and
 * // "api/statements-dataflow.md" (row 1 of STUB_CANNED_TABLE).
 * ```
 */
export class StubChatbotTransport implements ChatbotTransport {
  /** Discriminator — the real MCP client will report `"mcp"` instead. */
  public readonly kind = "stub" as const;

  /**
   * Answer a message from the canned table.
   *
   * @param input - {@link ChatbotSendInput}. `sessionId` should be a
   *   16-char lowercase hex string; anything else still resolves but
   *   the response text is annotated with an invalid-session note.
   * @returns A promise that resolves to a {@link ChatbotResponse}.
   *   Never rejects.
   */
  public async send(input: ChatbotSendInput): Promise<ChatbotResponse> {
    // Simulated latency. Never wraps in try/catch — `setTimeout` cannot
    // throw and the promise never rejects.
    await new Promise<void>((resolve) => {
      setTimeout(resolve, SIMULATED_LATENCY_MS);
    });

    const sessionInvalid = !SESSION_KEY_PATTERN.test(input.sessionId ?? "");
    const errorId = input.context?.errorId;

    // Lowercase once so keyword substring matching is case-insensitive and
    // stable across all rows.
    const lowered =
      typeof input.message === "string" ? input.message.toLowerCase() : "";

    const matchedRow = findFirstMatchingRow(lowered);

    // Compose the base response from the match (if any), the errorId (if
    // any), and finally the no-match fallback.
    let text: string;
    let citedDocs: string[];

    if (matchedRow !== undefined) {
      text = matchedRow.text;
      citedDocs = [...matchedRow.citedDocs];
      if (errorId !== undefined) {
        // Append the error-handling docs after the row's own citations,
        // deduplicated so a row that already cites either doc doesn't
        // list it twice.
        citedDocs = dedupePreservingOrder([...citedDocs, ...ERROR_HANDLING_DOCS]);
      }
    } else if (errorId !== undefined) {
      // No keyword match, but the caller pointed us at an error — cite
      // the error-handling docs as the primary response.
      text = ERROR_ONLY_TEXT;
      citedDocs = [...ERROR_HANDLING_DOCS];
    } else {
      // No match, no error context — the fallback stub message.
      text = NO_MATCH_TEXT;
      citedDocs = ["README.md"];
    }

    if (sessionInvalid) {
      text = `${text}${INVALID_SESSION_NOTE}`;
    }

    return { text, citedDocs };
  }
}

// ---------------------------------------------------------------------------
// Helpers (module-local — not exported)
// ---------------------------------------------------------------------------

/**
 * Return the first row in {@link STUB_CANNED_TABLE} that has ANY keyword
 * appearing as a substring of the (already-lowercased) message.
 *
 * @remarks
 * The table is small (4 rows, ≤ 2 keywords per row) so the O(rows × keywords)
 * scan is trivially fast. Iteration order is the table's declared order —
 * "first match wins" per the task spec.
 */
function findFirstMatchingRow(loweredMessage: string): StubCannedRow | undefined {
  if (loweredMessage.length === 0) return undefined;
  for (const row of STUB_CANNED_TABLE) {
    for (const keyword of row.keywords) {
      // Keywords in the table are authored lowercase (see stubCannedTable.ts),
      // but we defensively lowercase here so future edits can't silently
      // break case-insensitivity.
      if (loweredMessage.includes(keyword.toLowerCase())) {
        return row;
      }
    }
  }
  return undefined;
}

/**
 * De-duplicate an array of strings while preserving first-occurrence order.
 * Used to merge a matched row's `citedDocs` with the error-handling docs
 * without shuffling the visible order in the dock.
 */
function dedupePreservingOrder(paths: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const path of paths) {
    if (!seen.has(path)) {
      seen.add(path);
      out.push(path);
    }
  }
  return out;
}
